import React, { Component } from 'react';
import { Alert, Tab } from 'react-bootstrap';
import { connect } from 'react-redux';

import NCLRestrictedPlatformsAlert from '../../components/alerts/NCLRestrictedPlatformsAlert/NCLRestrictedPlatformsAlert';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import FAIcon from '../../components/FAIcon/FAIcon';
import Loading from '../../components/Loading/Loading';
import LoadingText from '../../components/Loading/LoadingText';
import MeatballDropdown from '../../components/MeatballDropdown/MeatballDropdown';
import {
	EditCustomFieldsModal
} from '../../components/modals/EditCustomFieldModal/EditCustomFieldsModal';
import Page from '../../components/Page/Page';
import StatefulTabs from '../../components/StatefulTabs/StatefulTabs';
import Title from '../../components/Title/Title';
import { assetConst } from '../../constants/assetConstants';
import { companyAgreementConstants } from '../../constants/companyAgreementConstants';
import { loadingConstants } from '../../constants/loadingConstants';
import { roleTypeCodes } from '../../constants/roleConstants';
import { permConst } from '../../constants/permConst';
import { platformCodes, platformFeatures } from '../../constants/platformConstants';
import { transferRightsConstants } from '../../constants/transferRightsConstants';
import { connectQueryData, useCompanyAgreementsQuery } from '../../hooks/queryHooks';
import { getCompanyProfile } from '../../services/companiesService';
import { getCustomFields } from '../../services/customFieldsService';
import {
	checkPartNumSet,
	getProductRetailPrice,
	getProductSummary,
	putToggleSupersededFlag,
	setProductStatus,
} from '../../services/productsService';
import { platformAgreements, getActiveAgreements } from '../../utils/companyUtils';
import { safeEval } from '../../utils/dataUtils';
import { displayString } from '../../utils/stringUtils';
import { isFeatureSunsetted } from '../../utils/sunsetFeaturesUtils';
import { cancelErrorToast, createMessageForError, toasterNotify } from '../../utils/toaster';
import {
	determineActiveRightTransfer,
	transferDomains
} from '../../utils/transferRightsUtils';
import { isAuthorized, isFeatureActiveForPlatform } from '../../utils/userUtils';
import AssignDistributionRightsModal from './modals/AssignDistributionRightsModal';
import EditPartSkuNumModal from './modals/EditPartSkuNumModal';
import PhysicalOrderChecklistModal from './modals/PhysicalOrderChecklistModal';
import TransferOwnershipModal from './modals/TransferOwnershipModal';
import {
	canShowCodeInBoxAssets,
	canShowMarketingAssets,
	canShowPackagingAssets,
	canShowPhysicalOrderChecklist,
	canShowProjections
} from './ProductProfile.helpers';
import AssetsTab from './Tabs/AssetsTab';
import DigitalCatalogTab from './Tabs/DigitalCatalogTab';
import GameConfigTab from './Tabs/GameConfigTab';
import LabelHistoryTab from './Tabs/LabelHistoryTab';
import OrdersTab from './Tabs/OrdersTab';
import ProductSummaryTab from './Tabs/ProductSummaryTab';
import ProjectionsTab from './Tabs/ProjectionsTab';
import SubmissionsTab from './Tabs/SubmissionsTab';

import './ProductProfile.css';

function mapStateToProps(state) {
	return {
		userProfile: state.authReducer.userProfile,
		platforms: state.referenceReducer.platforms?.content,
		platformsReady:
			state.referenceReducer.platforms?.meta.status === loadingConstants.COMPLETED,
		sunsetFeatures: state.referenceReducer.sunsetFeatures,
		sunsetFeaturesReady:
			state.referenceReducer.sunsetFeatures?.meta.status === loadingConstants.COMPLETED,
	};
}

const checkPrice = (userProfile, product) => {
	if (!isAuthorized(userProfile.permissions, [
		permConst.PRODUCT.VIEW.PRICE.COMPANY,
		permConst.PRODUCT.VIEW.PRICE.ALL
	])) {
		return false;
	}

	if (!safeEval(() => product.distribution.find((d) => d.right_type === 'RETAIL_PUB'))) {
		return false;
	}

	if (
		isAuthorized(userProfile.permissions, [permConst.PRODUCT.VIEW.PRICE.COMPANY]) &&
		product.distribution.find((d) => d.right_type === 'RETAIL_PUB').ndid_company_id !==
			userProfile.companyId
	) {
		return false;
	}

	if (isAuthorized(userProfile.permissions, [permConst.PRODUCT.VIEW.PRICE.ALL])
		&& !safeEval(() => product.noa_part_number)) {
		return false;
	}

	return true;
};

/**
 * For Switch products, return two boolean values on whether a user can assign a specific
 * distribution rights. This function checks the current transfer status of the product and if
 * user's company has access or not.
 * @param  {} product
 */
const determineAssignableDistributionRights = (product) => {

	const activeTransfer = product?.active_rights_transfer;
	const transferFlags = product?.eligible_transfer_flags;

	// If product is 3DS or WiiU, dist rights ARE NOT assignable.
	// https://jira.noa.com/browse/LDBR-1751
	if (
		product.system_family === platformCodes['Nintendo 3DS'] ||
		product.system_family === platformCodes['Wii U'] ||
		transferFlags.has_ownership === false
	) {
		return { digital: false, retail: false };
	}

	return {
		// if there is an active retail transfer, no digital transfer
		digital: !!(activeTransfer.digital_transfer_flag !== 1 && transferFlags.has_digital),
		// if there is an active digital transfer, no retail transfer
		retail: !!(activeTransfer.retail_transfer_flag !== 1 && transferFlags.has_retail),
	};
};

export class ProductProfile extends Component {
	constructor(props) {
		super(props);
		this.state = {
			activeTab: 'summary',
			isProductSummaryLoaded: false,
			isProductRetailPriceLoaded: false,
			isCompanyProfileLoaded: false,
			isProductSubmissionsLoaded: false,
			isCustomFieldsLoaded: false,
			isSubmitting: false,
			showModal: false,
			displayEditPartSkuNumButton: false,
			productId: '',
			product: null,
			submissions: [],
			mostRecentSubmissionId: null,
			submissionDetails: {},
			readOnly: false, // this state is only when ownership is being transferred
			summary: {
				alertMessageType: '',
				status: '',
				product_id: '',
				game_name: '',
				game_code: '',
				game_code_requested_date: '',
				platform_name: '',
				region_game_code: [],
				languages: [],
				noa_part_number: '',
				card_type: '',
				part_number_required_flag: '',
				publisher_part_number: '',
				price: '',
				rating: '',
				comment: '',
				product_distribution_type: '',
				distribution: [],
				canAssignDistRights: true,
				distRightAssignable: {
					retail: false,
					digital: false,
				},
				ebs_account_number: '',
				eligible_transfer_flags: {
					has_ownership: false,
					has_retail: false,
					has_digital: false
				},
				features: {
					online_mp: '',
					online_commerce: '',
					consumable_aoc: '',
					ugc: '',
					game_rating: ''
				}

			},
			digitalCatalogHasIssue: false
		};
		this.loadProductSummary = this.loadProductSummary.bind(this);
		this.loadCustomFields = this.loadCustomFields.bind(this);
		this.toggleModal = this.toggleModal.bind(this);
		this.toggleSupersededOverride = this.toggleSupersededOverride.bind(this);
		this.updateGameCodeField = this.updateGameCodeField.bind(this);
		this.updatePartNum = this.updatePartNum.bind(this);
		this.updateProductStatus = this.updateProductStatus.bind(this);
		this.onTabSelected = this.onTabSelected.bind(this);

		// add helper functions as instance methods
		this.canShowProjections = canShowProjections.bind(this);
	}

	componentDidMount() {
		this.componentDidUpdate();
	}

	componentDidUpdate(prevProps) {
		if (!prevProps || prevProps.match.params.id !== this.props.match.params.id) {
			const { match } = this.props;
			const productId =
				match.params && match.params.id > 0 ? match.params.id : null;
			this.setState({
				productId: productId
			});
			this.loadProductSummary(productId);
			this.loadCompanyProfile();
			this.loadCustomFields(productId);
		}
	}
	checkDigitalAgreement(platformCode) {
		if (!(platformCode in platformAgreements)) {
			this.setState({ noDigitalAgreement: false });
			return;
		}
		const { companyAgreementsQuery } = this.props;
		const digitalAgreements = getActiveAgreements(
			companyAgreementsQuery,
			companyAgreementConstants.TYPE.DIGITAL,
			platformCode,
		);
		this.setState({ noDigitalAgreement: !Array.isArray(digitalAgreements) || digitalAgreements.length === 0 });
	}

	hasDataLoaded() {
		const { platformsReady } = this.props;
		const {
			isProductSummaryLoaded,
			isCustomFieldsLoaded,
			isProductRetailPriceLoaded,
			isCompanyProfileLoaded,
			isCheckPartNumSetLoaded,
		} = this.state;

		return (
			isProductSummaryLoaded &&
			isCustomFieldsLoaded &&
			isProductRetailPriceLoaded &&
			(!this.canShowDigitalCatalog() || isCompanyProfileLoaded) &&
			isCheckPartNumSetLoaded &&
			platformsReady
		);
	}

	async updateProductStatus() {
		const { summary, productId } = this.state;
		let { match } = this.props;
		const newProductStatusValue =
			summary.retail_status === 'RETAIL_ACTIVE'
				? 'RETAIL_INACTIVE'
				: 'RETAIL_ACTIVE';
		const messageText =
			summary.retail_status === 'RETAIL_ACTIVE'
				? 'inactive'
				: 'active';

		const toastInstance = await toasterNotify(
			<><LoadingText inline className="m-0 float-right" /> Submitting status change</>,
			'info',
			null,
			{
				closeButton: false,
				autoClose: false,
			},
		);
		setProductStatus(match.params.id, newProductStatusValue)
			.then(() => {
				toastInstance.update(`The retail status has changed to ${messageText}`, 'success');
				this.loadProductSummary(productId);
			})
			.catch((error) => {
				cancelErrorToast(error);
				toastInstance.update(
					createMessageForError(error, 'setting new retail status'),
					'error',
					error,
				);
			});
	}

	async toggleSupersededOverride() {
		const { summary, productId } = this.state;
		const enable = summary.superseded_override_flag ? false : true;

		this.setState({ isSubmitting: true });
		const toastInstance = await toasterNotify(
			<><LoadingText inline className="m-0 float-right" /> Submitting supserseded override change</>,
			'info',
			null,
			{
				closeButton: false,
				autoClose: false,
			},
		);
		try {
			await putToggleSupersededFlag(summary.product_id, enable);
			toastInstance.update(
				`Allow superseded override has been ${enable ? 'enabled' : 'disabled'}`,
				'success',
			);
			this.loadProductSummary(productId);
		} catch (error) {
			cancelErrorToast(error);
			toastInstance.update(
				createMessageForError(error, 'changing the superseded override setting'),
				'error',
				error,
			);
		} finally {
			this.setState({ isSubmitting: false });
		}
	}

	generateSummary(summary) {
		if (summary) {
			if (
				!summary.company_name &&
				summary.distribution &&
				summary.distribution.length > 0
			) {
				const distRights = summary.distribution.find((distribution) => {
					return (
						distribution.right_type ===
							transferRightsConstants.DISTRIBUTION_TYPE.RETAIL ||
						distribution.right_type ===
							transferRightsConstants.DISTRIBUTION_TYPE.DIGITAL
					);
				});

				if (distRights) { 
					summary.company_name = distRights.company_name;
				}
			}
			summary.title = summary.game_name;
			if (summary.game_code) {
				summary.title += ` (${summary.game_code})`;
			}
		}
		return summary;
	}

	loadProductSummary(productId) {
		const { userProfile } = this.props;

		this.setState({
			isProductSummaryLoaded: false
		});
		getProductSummary(productId)
			.then((response) => {
				if (checkPrice(userProfile, response.data)) {
					this.loadProductRetailPrice(productId);
				} else {
					this.setState({
						isProductRetailPriceLoaded: true
					});
				}
				const summary = this.generateSummary(response.data);
				this.checkDigitalAgreement(summary.platform_code);

				this.setState(
					{
						product: response.data,
						summary,
						readOnly: determineActiveRightTransfer(
							response.data,
							transferDomains.PRODUCT_SUMMARY,
						),
						distRightAssignable: determineAssignableDistributionRights(response.data),
					},
					() => {
						if (
							isAuthorized(userProfile.permissions, [permConst.PRODUCT.EDIT.PARTSKU])
						) {
							this.displayEditPartSkuNumButton();
						} else {
							this.setState({
								isCheckPartNumSetLoaded: true,
							});
						}
					},
				);
			})
			.catch((e) => {
				if (e.response && e.response.status === 401) {
					this.setState({
						noDigitalAgreement: true,
						readOnly: true
					});
				} else {
					toasterNotify(
						createMessageForError(e, 'retrieving product data'),
						'error',
						e
					);
				}
				this.setState({
					summary: { error: e },
				});
			})
			.finally(() => {
				this.setState({
					isProductSummaryLoaded: true
				});
			});
	}

	loadCompanyProfile() {
		const { userProfile } = this.props;

		if (!this.canShowProductSummaryAndGameConfig(userProfile)) {
			return;
		}

		this.setState({
			isCompanyProfileLoaded: false
		});

		getCompanyProfile(userProfile.companyId)
			.then((response) => {
				this.setState({
					companyProfile: response.data
				});
			})
			.catch((e) => {
				toasterNotify(createMessageForError(e, 'retrieving company profile'), 'error', e);
			})
			.finally(() => {
				this.setState({
					isCompanyProfileLoaded: true
				});
			});
	}

	loadCustomFields(productId) {
		const { userProfile } = this.props;
		const canView = isAuthorized(userProfile.permissions,
			[permConst.CUSTOM_FIELD.VIEW.ALL]);
		if (!canView) {
			return this.setState({
				isCustomFieldsLoaded: true,
				customFields: null
			});
		}
		this.setState({
			isCustomFieldsLoaded: false
		});
		getCustomFields('product', String(productId))
			.then((response) => {
				this.setState({
					customFields: response.data
				});
			})
			.catch((error) => {
				toasterNotify(
					createMessageForError(error, 'retrieving custom fields'),
					'error',
					error
				);
			})
			.finally(() => {
				this.setState({
					isCustomFieldsLoaded: true
				});
			});
	}

	loadProductRetailPrice(productId) {
		this.setState({
			isProductRetailPriceLoaded: false
		});
		getProductRetailPrice(productId)
			.then((response) => {
				this.setState({
					productRetailPrice: response.data.retail_price
				});
			})
			.catch((error) => {
				toasterNotify('There was an error retrieving price', 'warning', error, {autoClose: 4000});
			})
			.finally(() => {
				this.setState({
					isProductRetailPriceLoaded: true
				});
			});
	}

	toggleModal(modalType, callback) {
		const type = this.state.showModal ? null : modalType;

		this.setState({
			showModal: !this.state.showModal,
			modalType: type,
			modalCallback: callback
		});
	}

	updateGameCodeField(updatedProductSummary) {
		this.setState({
			summary: updatedProductSummary
		});
	}

	updatePartNum(newValue) {
		let summ = this.state.summary;
		summ.publisher_part_number = newValue['part_number'];
		this.setState({
			summary: summ
		});
	}

	generateStatusTitleProps() {
		const retailStatus = this.state.summary.retail_status;
		let retailStatusClass = '';
		let retailStatusText = '';
		if (retailStatus) {
			retailStatusClass =
				retailStatus === 'RETAIL_ACTIVE'
					? 'status-success'
					: 'status-danger';
			retailStatusText = displayString(retailStatus);
		}
		const titleStatus = {};
		titleStatus['class'] = retailStatusClass;
		titleStatus['text'] = retailStatusText;
		return titleStatus;
	}

	displayEditPartSkuNumButton() {
		const { userProfile } = this.props;
		const platformCode = this.state.summary.platform_code;
		if (!isFeatureActiveForPlatform(userProfile, platformFeatures.PHYSICAL_ORDER, platformCode)) {
			this.setState({
				displayEditPartSkuNumButton: false,
				isCheckPartNumSetLoaded: true
			});
			return;
		}
		let display;
		checkPartNumSet(this.state.summary.product_id)
			.then((response) => {
				display = response.status === 200 && response.data !== false;
			})
			.catch(() => {
				display = false;
			})
			.finally(() => {
				this.setState({
					displayEditPartSkuNumButton: display,
					isCheckPartNumSetLoaded: true
				});
			});
	}

	displayAssignDistributionRightsButton() {
		const { userProfile } = this.props;
		const { summary } = this.state;
		return (
			isFeatureActiveForPlatform(
				userProfile,
				platformFeatures.DISTRIBUTION_TRANSFERS,
				summary.platform_code,
			) &&
			isAuthorized(userProfile.permissions, [
				permConst.PRODUCT.RIGHT_TRANSFER.REQUEST.COMPANY,
			]) &&
			summary?.eligible_transfer_flags?.has_ownership &&
			(summary?.eligible_transfer_flags?.has_retail ||
				summary?.eligible_transfer_flags?.has_digital)
		);
	}

	displayTransferOwnershipButton() {
		const { userProfile } = this.props;
		const { summary } = this.state;
		return (
			isAuthorized(userProfile.permissions, [permConst.PRODUCT.RIGHT_TRANSFER.REQUEST.COMPANY]) &&
			summary?.eligible_transfer_flags?.has_ownership
		);
	}

	displayRightsTransferAlert() {
		const { userProfile } = this.props;
		const { summary } = this.state;
		const { companyId } = userProfile;

		// internal user path
		if (isAuthorized(userProfile.permissions, [
			permConst.PRODUCT.VIEW.ALL.FIRST,
			permConst.PRODUCT.VIEW.ALL.THIRD
		])) {
			return summary.active_rights_transfer.retail_transfer_flag === 1 ||
				summary.active_rights_transfer.digital_transfer_flag === 1;
		}

		// external user path
		return (
			summary?.distribution?.some(
				(transfer) => transfer.right_type === 'RETAIL_PUB' && transfer.ndid_company_id === companyId,
			) && summary.active_rights_transfer.retail_transfer_flag === 1
		) || (
			summary?.distribution?.some(
				(transfer) => transfer.right_type === 'DIGITAL_PUB' && transfer.ndid_company_id === companyId,
			) && summary.active_rights_transfer.digital_transfer_flag === 1
		);
	}

	renderTitleButton() {
		const { userProfile, sunsetFeatures } = this.props;
		const { summary, isSubmitting, readOnly, distRightAssignable } = this.state;
		const { platform_name } = summary;

		const isPlatformSwitch = platform_name === 'Nintendo Switch';
		const canEditPublisherPartSku =
			isAuthorized(userProfile.permissions, [
				permConst.PRODUCT.EDIT.PARTSKU
			]) && this.state.displayEditPartSkuNumButton;
		const canEditCustomField =
			isAuthorized(userProfile.permissions, [
				permConst.CUSTOM_FIELD.VIEW.ALL
			]);

		const canAssignDistributionRights =
			this.displayAssignDistributionRightsButton();
		const assignDistributionRightsDisabled =
			!(distRightAssignable?.digital) && !(distRightAssignable?.retail);

		const canTransferOwnership =
			this.displayTransferOwnershipButton() &&
				!isPlatformSwitch &&
				!isFeatureSunsetted(sunsetFeatures?.content, 'ownership_transfers');
		const canEditProductStatus =
			isAuthorized(userProfile.permissions, [
				permConst.PRODUCT.EDIT.STATUS
			]) && summary.retail_status_editable;

		const canOverrideSuperseded = 
			isAuthorized(userProfile.permissions, [
				permConst.PRODUCT.EDIT.SUPERSEDED_OVERRIDE_FLAG.ALL
			]) &&
			('superseded_override_flag' in summary || summary.retail_status === 'SUPERSEDED');

		const showTitleButton =
			canEditPublisherPartSku ||
			canAssignDistributionRights ||
			canTransferOwnership ||
			canEditProductStatus ||
			canEditCustomField ||
			canOverrideSuperseded;

		if (!showTitleButton) {
			return null;
		}

		return (
			<>
				{platform_name !== '' && (
					<MeatballDropdown
						id="page-context-menu"
						toggleSize='lg'
					>
						{canEditCustomField && (
							<MeatballDropdown.Item
								eventKey="editCustomFieldsButton"
								id="editCustomFieldsButton"
								onSelect={(e) => this.toggleModal(e)}
							>
								Edit Custom Fields
							</MeatballDropdown.Item>
						)}
						{canEditPublisherPartSku && (
							<MeatballDropdown.Item
								eventKey="editSkuPartNum"
								id="editSkuPartNum"
								onSelect={(e) => this.toggleModal(e)}
								disabled={readOnly || determineActiveRightTransfer(
									summary,
									transferDomains.PROJECTION,
								)}
							>
								Edit Publisher Part/SKU Number
							</MeatballDropdown.Item>
						)}
						<MeatballDropdown.Divider />
						{canAssignDistributionRights && (
								<MeatballDropdown.Item
									eventKey="assignDistributionRightsButton"
									id="assignDistributionRightsButton"
									onSelect={(e) => this.toggleModal(e)}
									disabled={assignDistributionRightsDisabled}
								>
									<span className={!assignDistributionRightsDisabled ? 'text-danger' : null}>
										Assign Distribution Rights
									</span>
								</MeatballDropdown.Item>
							)}
						{canTransferOwnership && (
							<MeatballDropdown.Item
								eventKey="transferOwnershipButton"
								id="transferOwnershipButton"
								onSelect={(e) => {
									this.toggleModal(e);
								}}
								disabled={readOnly}
							>
								<span className="text-danger">
									Transfer Ownership
								</span>
							</MeatballDropdown.Item>
						)}
						{canEditProductStatus && (
							<MeatballDropdown.Item
								eventKey="6"
								onSelect={this.updateProductStatus}
								disabled={readOnly || isSubmitting || determineActiveRightTransfer(
									summary,
									transferDomains.RETAIL_ACTIVATION
								)}
							>
								{summary.retail_status === 'RETAIL_ACTIVE' ? (
									<span className={[(!readOnly && !determineActiveRightTransfer(
											summary,
											transferDomains.RETAIL_ACTIVATION
										)) ? 'text-danger' : null].join(' ')}>
										Inactivate Retail Product
									</span>
								) : (
									<span>Activate Retail Product</span>
								)}
							</MeatballDropdown.Item>
						)}
						{canOverrideSuperseded && 
							<><MeatballDropdown.Item
								eventKey="7"
								onSelect={this.toggleSupersededOverride}
								toggle={summary.superseded_override_flag}
								disabled={readOnly || isSubmitting}
							>
								Allow Superseded Override
							</MeatballDropdown.Item></>
						}
					</MeatballDropdown>
				)}
			</>
		);
	}

	renderAlertHeader() {
		const { companyAgreementsQuery, userProfile } = this.props;
		const { summary, noDigitalAgreement } = this.state;
		const isPublisher = (userProfile.roles || []).some(
			(role) => role.role_type_code === roleTypeCodes.EXTERNAL
		);
		const isNCL = isAuthorized(userProfile.permissions, [permConst.PRODUCT.VIEW.ALL.NCL]);
		const canAccessOrders = isAuthorized(userProfile.permissions, [
			permConst.PHYSICAL_ORDER.REVIEW.ALL,
			permConst.PHYSICAL_ORDER.APPROVE.ALL,
			permConst.PHYSICAL_ORDER.VIEW.COMPANY,
			permConst.DIGITAL_ORDER.VIEW.COMPANY,
		]);
		const canViewPackagingAssets =
			isAuthorized(userProfile.permissions, [
				permConst.PRODUCT.ASSET.PACKAGING.VIEW.ALL,
				permConst.PRODUCT.ASSET.PACKAGING.VIEW.COMPANY,
			]) &&
			isFeatureActiveForPlatform(userProfile, platformFeatures.PACKAGING_ASSETS, 'POT', 'view');
		const alerts = [];
		if (!companyAgreementsQuery && isPublisher) {
			alerts.push(
				<Alert variant='danger' key="alert_0">
					Agreements could not be retrieved and functions that require a valid agreement
					have been disabled. Reload or contact site support for assistance.
				</Alert>,
			);
		} else if (noDigitalAgreement && isPublisher) {
			alerts.push(
				<Alert variant='danger' key="alert_1">
					The Global Digital agreement has expired. Please renew the agreement in the
					Nintendo Developer Portal to proceed.
				</Alert>,
			);
		}
		if (this.displayRightsTransferAlert()) {
			alerts.push(
				<Alert className="alert-icon" variant='danger' key="alert_3">
					The product is pending a rights transfer. Some actions are disabled until the
					transfer is completed or rescinded.
				</Alert>,
			);
		}
		if (summary.platform_code === 'POT' && canViewPackagingAssets) {
			alerts.push(
				<Alert className="alert-icon" variant="info" key="alert_7">
					This product uses a 0GB cartridge. Please use the Full Key Card packaging
					elements, #28. Digital code orders are not available for this product type.
				</Alert>,
			);
		} else if (summary.platform_code === 'NXS') {
			alerts.push(
				<Alert className="alert-icon" variant="danger" key="alert_7">
					This product is a <b>Nintendo Switch 2 Enhanced</b> version of a{' '}
					<b>Nintendo Switch</b> product. Digital code orders are not available for this
					product type.
				</Alert>,
			);
		}
		if (isNCL) {
			alerts.push(
				<NCLRestrictedPlatformsAlert
					singlePlatform={summary.platform_code}
					key="alert_5"
				/>,
			);
		}
		if (canAccessOrders && summary.platform_code === 'HAC') {
			if (summary.superseded_override_flag === true) {
				alerts.push(
					<Alert className="alert-icon" variant="info" key="alert_6">
						An enhanced version of this product exists. A temporary override has been
						applied to allow orders for this <b>Nintendo Switch</b> version.
					</Alert>,
				);
			} else if (summary.retail_status === 'SUPERSEDED') {
				alerts.push(
					<Alert className="alert-icon" variant="danger" key="alert_6">
						This <b>Nintendo Switch</b> product has been superseded by a{' '}
						<b>Nintendo Switch 2 Enhanced</b> version. Orders for this product are no longer
						supported.
					</Alert>,
				);
			} else if (summary.related_products?.some(({ relation_type }) => relation_type === 'ENHANCED')) {
				alerts.push(
					<Alert className="alert-icon" variant="warning" key="alert_6">
						This <b>Nintendo Switch</b> product has been superseded by a{' '}
						<b>Nintendo Switch 2 Enhanced</b> version. Physical Orders for this product
						will not be supported after the Nintendo Switch 2 Enhanced version has been
						ordered.
					</Alert>,
				);
			}
		}
		if (alerts.length > 0) {
			return (
				<div>{alerts}</div>
			);
		}
		return null;
	}

	renderModal() {
		const { userProfile } = this.props;
		switch (this.state.modalType) {
			case 'editSkuPartNum':
				return (
					<EditPartSkuNumModal
						show={true}
						closeModal={this.toggleModal}
						productId={this.state.summary.product_id}
						partNum={this.state.summary.publisher_part_number}
						updatePartNumber={this.updatePartNum}
					/>
				);
			case 'physicalOrderChecklist':
				return (
					<PhysicalOrderChecklistModal
						productId={this.state.summary.product_id}
						closeModal={this.toggleModal}
						show={true}
					/>
				);
			case 'assignDistributionRightsButton':
				return (
					<AssignDistributionRightsModal
						userProfile={userProfile}
						closeModal={this.toggleModal}
						history={this.props.history}
						productSummary={this.state.summary}
						distRightAssignable={this.state.distRightAssignable}
					/>
				);
			case 'transferOwnershipButton':
				return (
					<TransferOwnershipModal
						userProfile={userProfile}
						closeModal={this.toggleModal}
						history={this.props.history}
						productSummary={this.state.summary}
					/>
				);
			case 'editCustomFieldsButton':
				return (
					<EditCustomFieldsModal
						show={true}
						onClose={this.toggleModal}
						onCompletion={this.loadCustomFields}
						customFields={this.state.customFields}
						entityId={this.state.summary.product_id}
					/>
				);
			default:
				console.log('[ProductProfile] INVALID MODAL TYPE'); // eslint-disable-line no-console
		}
	}

	onTabSelected(activeKey) {
		this.setState({
			activeTab: activeKey
		});
	}

	canShowSubmissions(userProfile) {
		const { summary } = this.state;
		const isProductOwner =
			summary &&
			summary.eligible_transfer_flags &&
			summary.eligible_transfer_flags.has_ownership;
		const isExternal = isAuthorized(userProfile.permissions, [
			permConst.PRODUCT.VIEW.COMPANY
		]);
		const isInternal = isAuthorized(userProfile.permissions, [
			permConst.PRODUCT.VIEW.ALL.FIRST,
			permConst.PRODUCT.VIEW.ALL.THIRD
		]);

		return isInternal || (isProductOwner && isExternal);
	}

	canShowProductSummaryAndGameConfig(userProfile) {
		// true for all users except NCL users
		return isAuthorized(userProfile.permissions, [
			permConst.PRODUCT.VIEW.ALL.CONFIDENTIAL,
			permConst.PRODUCT.VIEW.COMPANY,
			permConst.PRODUCT.VIEW.ALL.FIRST,
			permConst.PRODUCT.VIEW.ALL.THIRD,
		]);
	}

	// criteria

	// digital catalog is enabled for product's platform
	// AND 
	// 1. user is internal and has view.all privileges
	// -or-
	// 2a. company has digital dist rights for product AND
	// 2b. user has view.company permissions
	canShowDigitalCatalog() {
		const { distribution, platform_code } = this.state.summary;
		const { userProfile } = this.props;

		// platform features check
		if (
			!isFeatureActiveForPlatform(
				userProfile,
				platformFeatures.DIGITAL_CODES,
				platform_code,
				'view',
			)
		) {
			return false;
		}

		// internal users permissions check
		if (isAuthorized(userProfile.permissions, [
			permConst.DIGITAL_ORDER.VIEW.ALL.BASE,
			permConst.PRODUCT.DIGITAL_CATALOG.VIEW.ALL,
		])) {
			return true;
		}

		// external users permissions and agreements check
		if (isAuthorized(userProfile.permissions, [
			permConst.DIGITAL_ORDER.VIEW.COMPANY,
			permConst.PRODUCT.DIGITAL_CATALOG.VIEW.COMPANY
		])) {
			// check if user company has digital rights or owner rights
			return (
				distribution?.some(
					(right) =>
						[
							transferRightsConstants.DISTRIBUTION_TYPE.OWNERSHIP,
							transferRightsConstants.DISTRIBUTION_TYPE.DIGITAL,
						].includes(right.right_type) &&
						right.ndid_company_id === userProfile.companyId,
				)
			);
		}
		return false;
	}

	canShowOrders(userProfile) {
		const { summary } = this.state;
		return (
			['RETAIL_ACTIVE', 'RETAIL_INACTIVE'].includes(summary.retail_status) &&
			isAuthorized(userProfile.permissions, [
				permConst.ORDER.DETAIL.PRODUCT.VIEW.ALL,
				permConst.ORDER.DETAIL.PRODUCT.VIEW.COMPANY,
				permConst.PHYSICAL_ORDER.VIEW.ALL.BASE,
				permConst.PHYSICAL_ORDER.VIEW.COMPANY,
			]) &&
			isFeatureActiveForPlatform(
				userProfile,
				platformFeatures.PHYSICAL_ORDER,
				summary.platform_code,
				'view'
			)
		);
	}

	canShowLabelHistory(userProfile) {
		const { summary } = this.state;
		return (
			isFeatureActiveForPlatform(
				userProfile,
				platformFeatures.PACKAGING_ASSETS,
				summary.platform_code,
				'edit'
			) &&
			['RETAIL_ACTIVE', 'RETAIL_INACTIVE'].includes(summary.retail_status) &&
			isAuthorized(userProfile.permissions, [permConst.NCL.LABEL_QUEUE.VIEW])
		);
	}

	renderTabs() {
		const { userProfile } = this.props;
		const { product, productId, summary, activeTab, digitalCatalogHasIssue, productRetailPrice,
			customFields, readOnly } = this.state;

		const showPackagingAssets = canShowPackagingAssets(userProfile, product);
		const showMarketingAssets = canShowMarketingAssets(userProfile, product);
		const showProductSummaryAndGameConfig =
			this.canShowProductSummaryAndGameConfig(userProfile);
		const showCodeInBoxAssets = canShowCodeInBoxAssets(userProfile, summary);
		const showProjections = this.canShowProjections(userProfile, summary);
		const showSubmissions = this.canShowSubmissions(userProfile);
		const showDigitalCatalog = this.canShowDigitalCatalog();
		const showOrders = this.canShowOrders(userProfile);
		const showLabelHistory = this.canShowLabelHistory(userProfile);
		const showPhysicalOrderChecklist = canShowPhysicalOrderChecklist(userProfile, summary);
		
		return (
			<div className="Page__fill-space">
				<StatefulTabs onSelect={this.onTabSelected} transition={false} retainPageState>
					{showProductSummaryAndGameConfig && (
						<Tab eventKey="summary" title="Product Summary">
							<Page.ContentContainer>
								<ProductSummaryTab
									isSelected={activeTab === 'summary'}
									userProfile={userProfile}
									product={summary}
									productRetailPrice={productRetailPrice}
									toggleModal={this.toggleModal}
									readOnly={
										readOnly ||
										determineActiveRightTransfer(
											summary,
											transferDomains.PRODUCT_SUMMARY,
										)
									}
									sunsetFeatures={this.props.sunsetFeatures}
									customFields={customFields}
									showPhysicalOrderChecklist={showPhysicalOrderChecklist}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showProductSummaryAndGameConfig && (
						<Tab eventKey="game-config" title="Game Config">
							<Page.ContentContainer>
								<GameConfigTab
									isSelected={activeTab === 'game-config'}
									product={product}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showPackagingAssets && (
						<Tab eventKey="packaging" title="Packaging Assets">
							<Page.ContentContainer>
								<AssetsTab
									category={assetConst.CATEGORY.PACKAGING}
									isSelected={activeTab === 'packaging'}
									product={product}
									readOnly={
										(summary.retail_status === 'SUPERSEDED' &&
											summary.superseded_override_flag !== true) ||
										readOnly ||
										determineActiveRightTransfer(
											summary,
											transferDomains.ASSET_PACKAGING,
										)
									}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showMarketingAssets && (
						<Tab eventKey="marketing" title="Marketing Assets">
							<Page.ContentContainer>
								<AssetsTab
									category={assetConst.CATEGORY.MARKETING}
									isSelected={activeTab === 'marketing'}
									product={product}
									readOnly={
										readOnly ||
										determineActiveRightTransfer(
											summary,
											transferDomains.ASSET_MARKETING,
										)
									}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showCodeInBoxAssets && (
						<Tab eventKey="cib-assets" title="Code-In-Box Assets">
							<Page.ContentContainer>
								<AssetsTab
									category={assetConst.CATEGORY.CODE_IN_BOX}
									isSelected={activeTab === 'cib-assets'}
									product={product}
									readOnly={
										(summary.retail_status === 'SUPERSEDED' &&
											summary.superseded_override_flag !== true) ||
										readOnly ||
										determineActiveRightTransfer(
											summary,
											transferDomains.CODE_IN_BOX,
										)
									}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showProjections && (
						<Tab eventKey="projections" title="Projections">
							<Page.ContentContainer>
								<ProjectionsTab
									isSelected={activeTab === 'projections'}
									productId={productId}
									userProfile={userProfile}
									history={this.props.history}
									distribution={summary.distribution}
									readOnly={
										readOnly ||
										determineActiveRightTransfer(
											summary,
											transferDomains.PROJECTION,
										)
									}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showSubmissions && (
						<Tab eventKey="submissions" title="Submissions">
							<Page.ContentContainer>
								<SubmissionsTab
									isSelected={activeTab === 'submissions'}
									product={product}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showDigitalCatalog && (
						<Tab
							eventKey="digital-catalog"
							title={
								<>
									Digital Catalog{' '}
									{digitalCatalogHasIssue && (
										<FAIcon name="exclamation-circle" className="text-danger" />
									)}{' '}
								</>
							}
						>
							<Page.ContentContainer>
								<DigitalCatalogTab
									isSelected={activeTab === 'digital-catalog'}
									productId={productId}
									product={summary}
									flagIssue={() => {
										this.setState({ digitalCatalogHasIssue: true });
									}}
									rightsHolders={summary.distribution}
									productPlatformCode={summary.platform_code}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showOrders && (
						<Tab eventKey="orders" title="Orders">
							<Page.ContentContainer>
								<OrdersTab
									isSelected={activeTab === 'orders'}
									productId={productId}
									rightsHolders={summary.distribution}
									showPhysicalOrderChecklist={showPhysicalOrderChecklist}
									toggleModal={this.toggleModal}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
					{showLabelHistory && (
						<Tab eventKey="label-history" title="Label History">
							<Page.ContentContainer>
								<LabelHistoryTab
									isSelected={activeTab === 'label-history'}
									productId={productId}
								/>
							</Page.ContentContainer>
						</Tab>
					)}
				</StatefulTabs>
			</div>
		);
	}

	render() {
		const { summary, isProductSummaryLoaded } = this.state;
		
		const isLoading = !this.hasDataLoaded();
		const isFault = isProductSummaryLoaded && summary.error;
		return (
			<Page documentTitle={summary.title || ''} fault={isFault}>
				{isLoading ? (
					<Loading />
				) : (
					<>
						{this.renderAlertHeader()}
						<Breadcrumb>
							<Breadcrumb.Item to="/products">Products</Breadcrumb.Item>
							<Breadcrumb.Item active>{summary.title}</Breadcrumb.Item>
						</Breadcrumb>
						<Title
							title={summary.title}
							subtitle={summary.platform_name}
							status={this.generateStatusTitleProps()}
							button={this.renderTitleButton()}
						/>
						{this.renderTabs()}

						{this.state.showModal && this.state.modalType
							? this.renderModal()
							: null}
					</>
				)}
			</Page>
		);
	}
}

export default connectQueryData(
	connect(mapStateToProps)(ProductProfile), 
	useCompanyAgreementsQuery, 
	'companyAgreementsQuery',
	true,
);
